import React from 'react'
import { useTranslation } from 'next-i18next'
import { MultiStepModal, useDialogControllers } from '@machobear/design-system'
import { Form, Formik, FormikHelpers, FormikProps } from 'formik'

import ENV_VARS from '@/config'
import { registerRenter } from '@/models/auth/ports'
import { useDispatch } from '@/lib/redux'
import Yup from '@/lib/yup'
import handleSession from '@/utils/handle-session'
import usePasswordRequirements from '@/hooks/use-password-requirements'
import { useDSLogin } from '@/features/Auth/Context/ds-login-provider'
import DSLoginModal from '@/features/Auth/login-modal/ds-login-modal'
import { formatUserRegistrationValues } from '@/features/Auth/utils'
import { WarningModal } from '@/components/ds-warning-modal'

import ApplyFlowStepOne from './apply-flow-step-one'
import ApplyFlowStepThree from './apply-flow-step-three'
import ApplyFlowStepTwo from './apply-flow-step-two'

const { DASHBOARD_URL } = ENV_VARS

type Form = {
  token?: string
  first_name: string
  last_name: string
  email: string
  password?: string
  agree_to_terms: boolean
}

export type MyFormValues = {
  first_name: string
  last_name: string
  password: string
  email: string
  agree_to_terms: boolean
}

type ApplyNowProps = {
  listingId: string
  onClose: () => void
  visible: boolean
  eventId?: string
  showModal: () => void
}

const ApplyNowStepModal: React.FC<ApplyNowProps> = ({
  listingId,
  eventId = 'web-auth-switch-to-login',
  onClose: closeApplyNow,
  visible: visibleApplyNow,
  showModal: showApplyNowModal
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { visible, showModal, closeModal } = useDialogControllers({
    warning: false,
    login: false
  })
  const { setAfterLogin } = useDSLogin()
  const { requirements, checkRequirements } = usePasswordRequirements()

  const handleOpenAuthModal = () => {
    const applicationUrl = `${DASHBOARD_URL}/application/create/${listingId}/complete`
    setAfterLogin(() => window.open(applicationUrl, '_blank'))
    closeModal('warning')()
    showModal('login')()
  }

  const initialValues: MyFormValues = {
    first_name: '',
    last_name: '',
    password: '',
    email: '',
    agree_to_terms: true
  }

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .matches(
        /^[ A-Za-zÀ-Ÿ-_]*$/,
        t(
          'b.profile.name.special_characters.error',
          'Cannot include special characters'
        )
      )
      .required(t('b.error.required.text', 'Required')),
    last_name: Yup.string()
      .matches(
        /^[ A-Za-zÀ-Ÿ-_]*$/,
        t(
          'b.profile.name.special_characters.error',
          'Cannot include special characters'
        )
      )
      .required(t('b.error.required.text', 'Required')),
    password: Yup.string()
      .min(
        8,
        t(
          'b.auth.edit.password_requirements.error',
          'Missing password requirements below'
        )
      )
      .matches(
        /^\S*$/,
        t(
          'b.auth.edit.password_whitespace.error',
          'Whitespace characters not allowed'
        )
      )
      .matches(
        /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^{}()[\]./\\,>~+<'"`|\-=_;:&*?])[A-Za-z\d!@#$%^{}()[\]./\\,>~+<'"`|\-=_;:&*?]+$/,
        t(
          'b.auth.edit.password_requirements.error',
          'Missing password requirements below'
        )
      )
      .required(),
    email: Yup.string().email().required(t('b.error.required.text', 'Required'))
  })

  const onSubmit = async (
    values: Form,
    { setSubmitting }: FormikHelpers<Form>
  ) => {
    const formattedValues = formatUserRegistrationValues(values, {})
    const { response, body } = await registerRenter({
      body: formattedValues
    })

    setSubmitting(false)
    closeApplyNow()
    if (!response.ok) {
      if (
        body?.message === 'phone number or email already exists in the system'
      ) {
        return showModal('warning')()
      }
    }

    await handleSession(dispatch.session.loadSession())

    window.dataLayer.push({
      event: 'listing-apply-password-create-app-submit',
      userEmail: values.email,
      userFirstName: values.first_name,
      userLastName: values.last_name
    })

    if (typeof listingId !== 'undefined') {
      window.open(
        `${DASHBOARD_URL}/application/create/${listingId}/complete`,
        '_blank'
      )
    }
  }

  const handleCloseLoginModal = () => {
    closeModal('login')()
    closeApplyNow()
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({
          values,
          errors,
          submitForm,
          isSubmitting,
          resetForm,
          validateField,
          setTouched
        }: FormikProps<Form>) => {
          const handleTryAgain = () => {
            resetForm()
            closeModal('warning')()
            showApplyNowModal()
          }
          return (
            <>
              <MultiStepModal
                id="applyNowWizard"
                visible={visibleApplyNow}
                handleClose={closeApplyNow}
                showButtonIcons
                initialStep={0}
              >
                <MultiStepModal.Step
                  nextText={t('common.next', 'Next')}
                  backText={t('common.back', 'Back')}
                  nextAction={({ goNext }: { goNext: () => void }) => {
                    window?.dataLayer?.push({
                      event: 'city_page_click_applynow_name_continue'
                    })

                    const validateInputs = () => {
                      setTouched({ first_name: true, last_name: true })
                      validateField('first_name')
                      validateField('last_name')
                    }
                    values.first_name && values.last_name
                      ? goNext()
                      : validateInputs()
                  }}
                >
                  <ApplyFlowStepOne handleOpenAuthModal={handleOpenAuthModal} />
                </MultiStepModal.Step>
                <MultiStepModal.Step
                  nextText={t('common.next', 'Next')}
                  backText={t('common.back', 'Back')}
                  nextAction={({ goNext }: { goNext: () => void }) => {
                    window?.dataLayer?.push({
                      event: 'city_page_click_applynow_email_continue'
                    })
                    const validateInput = () => {
                      setTouched({ email: true })
                      validateField('email')
                    }

                    !errors.email ? goNext() : validateInput()
                  }}
                >
                  <ApplyFlowStepTwo />
                </MultiStepModal.Step>
                <MultiStepModal.Step
                  nextText={t(
                    'r.auth.create.create_application.button',
                    'Create Application'
                  )}
                  backText={t('common.back', 'Back')}
                  nextAction={() => {
                    window?.dataLayer?.push({
                      event: 'city_page_click_applynow_password_createapp'
                    })

                    submitForm()
                  }}
                  nextButtonDisabled={isSubmitting}
                >
                  <ApplyFlowStepThree
                    requirements={requirements}
                    checkRequirements={checkRequirements}
                  />
                </MultiStepModal.Step>
              </MultiStepModal>
              <DSLoginModal
                visible={visible.login}
                onClose={handleCloseLoginModal}
                mode="login"
                t={t}
                eventId={eventId}
              />
              <WarningModal
                eventId="web-signup-submit-renter-apply-emailtaken"
                visible={visible.warning}
                closeModal={closeModal('warning')}
                title={t(
                  'b.auth.create.existing_email.title',
                  'Existing email'
                )}
                description={t(
                  'b.auth.create.email_taken.text',
                  'This email is already taken, if it belongs to you please log in.'
                )}
                button={{
                  text: t('common.login', 'Log In'),
                  onClick: handleOpenAuthModal
                }}
                secondaryButton={{
                  text: t('b.auth.create.try_again.button', 'Try Again'),
                  onClick: handleTryAgain
                }}
              />
            </>
          )
        }}
      </Formik>
    </>
  )
}

export default ApplyNowStepModal
